import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import { createFormDataNested } from "../../../utils/form-data/create-form-data-nested"
import type {
  CalendarAttachment,
  CalendarInvitationSync,
  Event
} from "../types/types.ts"
import {
  type CalendarFilterOptionsData,
  type CreateEventAttachmentData,
  type CreateEventData,
  type DeleteEventData,
  type EventWithConflict,
  type GetAvailableSlotsParams,
  type GetEventsParams,
  type PartialUpdateEventData,
  type SearchEventItem,
  type SearchLeadItem,
  type SearchPaginatedParams,
  type SearchParams,
  type SearchParticipantItem,
  type SearchProjectItem,
  type UpdateEventData
} from "./services.types.ts"

const CALENDAR_BASE_URL = "/calendars/calendars/events"

const PATHS = {
  // Calendar Events
  getEvents: `${CALENDAR_BASE_URL}/`,
  createEvent: `${CALENDAR_BASE_URL}/`,
  getEventDetails: (id: string) => `${CALENDAR_BASE_URL}/${id}/`,
  updateEvent: (id: string) => `${CALENDAR_BASE_URL}/${id}/`,
  partialUpdateEvent: (id: string) => `${CALENDAR_BASE_URL}/${id}/`,
  deleteEvent: (id: string) => `${CALENDAR_BASE_URL}/${id}/`,
  getEventAttachments: (id: string) =>
    `${CALENDAR_BASE_URL}/${id}/attachments/`,
  createEventAttachment: (id: string) =>
    `${CALENDAR_BASE_URL}/${id}/attachments/`,
  completeEvent: (id: string) => `${CALENDAR_BASE_URL}/${id}/complete/`,
  inviteCustomer: (id: string) =>
    `${CALENDAR_BASE_URL}/${id}/invite-customer/`,
  retryInvitations: (id: string) =>
    `${CALENDAR_BASE_URL}/${id}/retry-invitations/`,
  searchEvents: `${CALENDAR_BASE_URL}/autocomplete/`,
  availableSlots: `${CALENDAR_BASE_URL}/available-slots/`,
  //
  filterOptions: `${CALENDAR_BASE_URL}/filter_options/`,
  searchLeads: `${CALENDAR_BASE_URL}/lead-search/`,
  searchProjects: `${CALENDAR_BASE_URL}/project-search/`
}

// Calendar Events API
export const getEvents = (params?: GetEventsParams) =>
  request.api.get<BasePagination<Event>>(PATHS.getEvents, params)

export const getAvailableSlots = (params: GetAvailableSlotsParams) =>
  request.api.get<BaseApiResponse<unknown>>(PATHS.availableSlots, params)

export const createEvent = (data: CreateEventData) => {
  if (!data.file_list?.length) {
    return request.api.post<BaseApiResponse<EventWithConflict>>(
      PATHS.createEvent,
      data
    )
  }

  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(data, "file")

  return request.api.post<BaseApiResponse<EventWithConflict>>(
    PATHS.createEvent,
    formData,
    { headers }
  )
}

export const getEventDetails = (eventId: string) =>
  request.api.get<BaseApiResponse<Event>>(PATHS.getEventDetails(eventId))

export const updateEvent = ({ id, ...data }: UpdateEventData) =>
  request.api.put<BaseApiResponse<EventWithConflict>>(
    PATHS.updateEvent(id),
    data
  )

export const partialUpdateEvent = ({ id, ...data }: PartialUpdateEventData) => {
  if (!data.file_list?.length) {
    return request.api.patch<BaseApiResponse<EventWithConflict>>(
      PATHS.partialUpdateEvent(id),
      data
    )
  }

  const headers = { "Content-Type": "multipart/form-data" }
  const multipartData =
    data.project === null ? { ...data, project: "" } : data
  const formData = createFormDataNested(multipartData, "file")

  return request.api.patch<BaseApiResponse<EventWithConflict>>(
    PATHS.partialUpdateEvent(id),
    formData,
    { headers }
  )
}

export const deleteEvent = (data: DeleteEventData) =>
  request.api.delete<BaseApiResponse<null>>(PATHS.deleteEvent(data.id))

export const inviteCustomer = ({
  id,
  email
}: {
  id: string
  email?: string | null
}) =>
  request.api.post<BaseApiResponse<null>>(
    PATHS.inviteCustomer(id),
    email ? { email } : {}
  )

// Re-sends the invitations and cancellations this appointment still owes.
// It replays what was recorded rather than rendering afresh, so a retry cannot
// tell a recipient something different from what the failed attempt would have.
export const retryInvitations = (id: string) =>
  request.api.post<
    BaseApiResponse<{ sent: number; invitation_sync: CalendarInvitationSync }>
  >(PATHS.retryInvitations(id), {})

export const getEventAttachments = (eventId: string) =>
  request.api.get<BaseApiResponse<CalendarAttachment[]>>(
    PATHS.getEventAttachments(eventId)
  )

export const createEventAttachment = ({
  id,
  ...data
}: CreateEventAttachmentData) => {
  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(data, "file")

  return request.api.post<BaseApiResponse<CalendarAttachment>>(
    PATHS.createEventAttachment(id),
    formData,
    { headers }
  )
}

export const completeEvent = (eventId: string) =>
  request.api.post<BaseApiResponse<null>>(PATHS.completeEvent(eventId))

export const searchEvents = (params: SearchParams) =>
  request.api.get<{
    events: SearchEventItem[]
    participants: SearchParticipantItem[]
  }>(PATHS.searchEvents, params)

export const searchLeads = (params: SearchPaginatedParams) =>
  request.api.get<BasePagination<SearchLeadItem>, BaseApiResponse<void>>(
    PATHS.searchLeads,
    params
  )

export const searchProjects = (params: SearchPaginatedParams) =>
  request.api.get<BasePagination<SearchProjectItem>, BaseApiResponse<void>>(
    PATHS.searchProjects,
    params
  )

export const getCalendarFilterOptions = () =>
  request.api.get<CalendarFilterOptionsData>(PATHS.filterOptions)
